// Bridge to the OverwolfStore client APIs over postMessage.
//
// The store website runs inside an iframe whose parent (the Overwolf app shell)
// is a DIFFERENT origin, so the iframe cannot touch the shell's `OverwolfStore`
// global directly — the browser blocks all cross-origin frame access. Instead we
// exchange messages with the shell: the store posts a request, the shell calls
// `OverwolfStore.apps.*` / `overwolf.profile.*` on its own window and posts the
// result back.
//
// Message contract (see also the shell-side implementation):
//   store (iframe) -> shell:
//     { source: 'ow-store', kind: 'request', requestId, channel, args }
//     { source: 'ow-store', kind: 'ping', requestId }
//   shell -> store (iframe):
//     { source: 'ow-store-host', kind: 'response', requestId, result?, error? }
//     { source: 'ow-store-host', kind: 'ready' }   // may be sent unsolicited on load
//     { source: 'ow-store-host', kind: 'event', event, payload }  // unsolicited app
//       install/uninstall notifications, may arrive any time after mount

// Channels the store can invoke on the shell. The enum (and its on-the-wire
// string values) is shared with the shell's handler map in
// packages/ow-app/src/desktop/store-bridge.ts (see overwolf-store-bridge.md).
import {
  BridgeChannel,
  OverwolfInstallOrigin,
} from '@ow/shared/store-bridge/bridge-channel';

// Source tags identifying each side of the conversation.
export const OW_STORE_MSG_SOURCE = 'ow-store';
export const OW_STORE_HOST_MSG_SOURCE = 'ow-store-host';

const INSTALLED_STATE = 'Installed';
const REQUEST_TIMEOUT_MS = 10000;
const PING_TIMEOUT_MS = 1000;

// Shell UI triggers use a `type` field (not the request/response `channel`), are
// fire-and-forget, and open a native modal overlay in the shell. Confirmed
// contract: post { source: 'ow-store', type: 'show-in-app-login', theme } to the
// parent; the shell validates our origin and opens its native login overlay.
// (Reading the signed-in user and reacting to login-state changes is done
// directly via the injected `overwolf.profile.*` API — see overwolf-api.service.)
const SHOW_IN_APP_LOGIN_TYPE = 'show-in-app-login';

// A single entry returned by `OverwolfStore.apps.getLocalApps`. The client
// returns many more fields per app; only the ones the store website consumes
// are typed here.
export interface LocalApp {
  UID: string;
  Name: string;
  InstallState: string;
}

// Pushed by the shell when an app is installed / uninstalled on the machine,
// independent of any request we made. `payload` is the raw native event payload
// — its shape is not finalized, so consumers should reconcile from
// `getLocalApps` rather than depend on specific fields.
// Known event names are 'app-installed' / 'app-uninstalled'; typed as string
// since the shell may add more and the contract isn't finalized.
export interface OverwolfStoreAppsChangedEvent {
  event: string;
  payload: unknown;
}

// Progress/lifecycle of an Overwolf-Electron app download, relayed from the
// shell's background download service. `appId` identifies which app (and thus
// which download button) the event refers to. `progress` is a percentage string
// on 'download-progress'; `reason` carries the error text on 'download-error'.
export type ElectronDownloadEventName =
  | 'download-start'
  | 'download-progress'
  | 'download-complete'
  | 'download-error';

export interface ElectronDownloadEvent {
  event: ElectronDownloadEventName;
  appId: string;
  progress?: string;
  reason?: string;
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

const pendingRequests = new Map<string, PendingRequest>();
const readyListeners = new Set<(ready: boolean) => void>();
const appsChangedListeners = new Set<
  (event: OverwolfStoreAppsChangedEvent) => void
>();
const downloadListeners = new Set<(event: ElectronDownloadEvent) => void>();

let listenerAttached = false;
let hostReady = false;
let requestCounter = 0;
let handshakeStarted = false;

// ---------------------------------------------------------------------------
const isEmbedded = (): boolean => {
  return typeof window !== 'undefined' && window.parent !== window;
};

// ---------------------------------------------------------------------------
const generateRequestId = (): string => {
  requestCounter += 1;
  return `${OW_STORE_MSG_SOURCE}-${requestCounter}-${Date.now()}`;
};

// ---------------------------------------------------------------------------
const setHostReady = (ready: boolean): void => {
  if (hostReady === ready) {
    return;
  }

  hostReady = ready;
  readyListeners.forEach((listener) => listener(ready));
};

// ---------------------------------------------------------------------------
const notifyAppsChanged = (event: OverwolfStoreAppsChangedEvent): void => {
  appsChangedListeners.forEach((listener) => listener(event));
};

// ---------------------------------------------------------------------------
const notifyDownload = (event: ElectronDownloadEvent): void => {
  downloadListeners.forEach((listener) => listener(event));
};

// ---------------------------------------------------------------------------
const isDownloadEvent = (name: unknown): name is ElectronDownloadEventName =>
  typeof name === 'string' && name.startsWith('download-');

// ---------------------------------------------------------------------------
const handleMessage = (event: MessageEvent): void => {
  const data = event.data;

  // Only trust messages coming from our direct parent (the shell). We can't
  // compare origins (we don't know the shell's), but the source frame check
  // prevents unrelated windows from spoofing responses.
  if (event.source !== window.parent) {
    return;
  }

  if (!data || data.source !== OW_STORE_HOST_MSG_SOURCE) {
    return;
  }

  if (data.kind === 'ready') {
    setHostReady(true);
    return;
  }

  if (data.kind === 'event') {
    // Unsolicited notification from the shell. Receiving one also proves the
    // shell is present and listening.
    setHostReady(true);

    // Download lifecycle events carry a flat { appId, progress?, reason? }
    // payload; route them to the download listeners. Everything else is an
    // install/uninstall notification.
    if (isDownloadEvent(data.event)) {
      const payload = (data.payload ?? {}) as {
        appId?: string;
        progress?: string;
        reason?: string;
      };
      notifyDownload({
        event: data.event,
        appId: payload.appId ?? '',
        progress: payload.progress,
        reason: payload.reason,
      });
      return;
    }

    notifyAppsChanged({ event: data.event, payload: data.payload });
    return;
  }

  if (data.kind === 'response' && typeof data.requestId === 'string') {
    // Any valid response also proves the shell is present and listening.
    setHostReady(true);

    const pending = pendingRequests.get(data.requestId);
    if (!pending) {
      return;
    }

    clearTimeout(pending.timeoutId);
    pendingRequests.delete(data.requestId);

    if (data.error) {
      pending.reject(new Error(String(data.error)));
    } else {
      pending.resolve(data.result);
    }
    return;
  }
};

// ---------------------------------------------------------------------------
const ensureListener = (): void => {
  if (listenerAttached || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('message', handleMessage);
  listenerAttached = true;
};

// ---------------------------------------------------------------------------
const sendRequest = <T>(
  channel: BridgeChannel,
  args: Record<string, unknown> = {},
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    if (!isEmbedded()) {
      reject(new Error('OverwolfStore bridge unavailable (not embedded)'));
      return;
    }

    ensureListener();

    const requestId = generateRequestId();
    const kind = channel === BridgeChannel.Ping ? 'ping' : 'request';

    const timeoutId = setTimeout(() => {
      pendingRequests.delete(requestId);
      reject(new Error(`OverwolfStore request timed out: ${channel}`));
    }, timeoutMs);

    pendingRequests.set(requestId, {
      resolve: resolve as (value: unknown) => void,
      reject,
      timeoutId,
    });

    // targetOrigin '*' because the shell origin is unknown to us; the payload
    // carries no secrets and the shell must validate our origin on its side.
    window.parent.postMessage(
      { source: OW_STORE_MSG_SOURCE, kind, requestId, channel, args },
      '*',
    );
  });
};

// ---------------------------------------------------------------------------
// Subscribe to shell-readiness. Immediately emits the current state, and again
// whenever it changes. Returns an unsubscribe function.
export const subscribeOverwolfStoreReady = (
  listener: (ready: boolean) => void,
): (() => void) => {
  readyListeners.add(listener);
  listener(hostReady);

  return () => {
    readyListeners.delete(listener);
  };
};

// ---------------------------------------------------------------------------
// Subscribe to unsolicited app install/uninstall events pushed by the shell.
// Returns an unsubscribe function.
export const subscribeOverwolfStoreAppsChanged = (
  listener: (event: OverwolfStoreAppsChangedEvent) => void,
): (() => void) => {
  appsChangedListeners.add(listener);

  return () => {
    appsChangedListeners.delete(listener);
  };
};

// ---------------------------------------------------------------------------
// Subscribe to Overwolf-Electron download lifecycle events relayed by the shell
// (start / progress / complete / error). Returns an unsubscribe function.
export const subscribeOverwolfStoreDownload = (
  listener: (event: ElectronDownloadEvent) => void,
): (() => void) => {
  downloadListeners.add(listener);

  return () => {
    downloadListeners.delete(listener);
  };
};

// ---------------------------------------------------------------------------
// Asks the shell whether the bridge is available. Resolves false (rather than
// rejecting) on timeout / when not embedded, so callers can treat it as a plain
// capability check.
export const pingOverwolfStore = async (): Promise<boolean> => {
  if (!isEmbedded()) {
    return false;
  }

  try {
    await sendRequest(BridgeChannel.Ping, {}, PING_TIMEOUT_MS);
    return true;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Starts the readiness handshake exactly once for the whole app: attaches the
// message listener (so an unsolicited `ready` is caught) and pings the shell for
// a bounded window in case it came up first. Subsequent calls are no-ops.
// Components observe the result via `subscribeOverwolfStoreReady`.
export const ensureOverwolfStoreHandshake = (): void => {
  if (handshakeStarted || typeof window === 'undefined' || !isEmbedded()) {
    return;
  }

  handshakeStarted = true;
  ensureListener();

  let attempts = 0;
  const maxAttempts = 10;

  const tryPing = async (): Promise<void> => {
    if (hostReady) {
      return;
    }

    const ready = await pingOverwolfStore();
    if (ready || hostReady) {
      return;
    }

    attempts += 1;
    if (attempts < maxAttempts) {
      setTimeout(() => {
        void tryPing();
      }, 500);
    }
  };

  void tryPing();
};

// ---------------------------------------------------------------------------
// Resolves with every app the local Overwolf client knows about (installed or
// not). Resolves with an empty list when the bridge is unavailable or errors,
// so callers never have to special-case the non-Overwolf path.
const getLocalApps = async (): Promise<LocalApp[]> => {
  try {
    const result = await sendRequest<LocalApp[]>(
      BridgeChannel.GetInstalledApps,
    );
    return Array.isArray(result) ? result : [];
  } catch {
    return [];
  }
};

// ---------------------------------------------------------------------------
// The set of app UIDs currently installed on the machine.
export const getInstalledAppIds = async (): Promise<Set<string>> => {
  const apps = await getLocalApps();
  const ids = new Set<string>();

  for (const app of apps) {
    if (app.UID && app.InstallState === INSTALLED_STATE) {
      ids.add(app.UID);
    }
  }

  return ids;
};

// ---------------------------------------------------------------------------
// Fire-and-forget: the shell acknowledges receipt; we don't wait for the
// install / launch itself to finish. `origin` is forwarded unchanged as the
// native OverwolfStore.apps.installApp call's 3rd argument, for the client's
// own install-source analytics (see use-download-click-handler).
export const installApp = (
  uid: string,
  origin?: OverwolfInstallOrigin,
): void => {
  void sendRequest(BridgeChannel.InstallApp, { uid, origin }).catch(
    () => undefined,
  );
};

// ---------------------------------------------------------------------------
export const launchApp = (uid: string): void => {
  void sendRequest(BridgeChannel.LaunchApp, { uid }).catch(() => undefined);
};

// ---------------------------------------------------------------------------
export const uninstallApp = (uid: string): void => {
  void sendRequest(BridgeChannel.UninstallApp, { uid }).catch(() => undefined);
};

// ---------------------------------------------------------------------------
// Asks the shell to place text on the OS clipboard via the native client
// (overwolf.utils.placeOnClipboard, reached through odk-ts in the shell). The
// embedded store can't touch overwolf.utils from its cross-origin frame.
// Fire-and-forget, mirroring installApp / launchApp.
export const placeOnClipboard = (text: string): void => {
  void sendRequest(BridgeChannel.PlaceOnClipboard, { text }).catch(
    () => undefined,
  );
};

// ---------------------------------------------------------------------------
// Ask the shell to open Overwolf settings (optionally a specific tab). The
// embedded store can't reach the overwolf://settings protocol from its
// cross-origin frame, so the shell navigates there on its behalf — reliable
// native navigation instead of the browser deep-link dance. Fire-and-forget,
// mirroring installApp / launchApp.
export const openSettings = (settingsTab?: string): void => {
  void sendRequest(BridgeChannel.OpenSettings, { settingsTab }).catch(
    () => undefined,
  );
};

// ---------------------------------------------------------------------------
// Overwolf-Electron app: ask the shell to download the installer and launch it.
// Unlike install/launch (which go through the native OverwolfStore by UID), an
// Electron app ships as a downloadable .exe — the shell downloads it to the
// Downloads folder via the native downloader plugin and runs it once complete.
// Fire-and-forget: the shell acks receipt, not download completion.
export const downloadApp = (
  url: string,
  appId: string,
  appName?: string,
): void => {
  void sendRequest(BridgeChannel.DownloadApp, { url, appId, appName }).catch(
    () => undefined,
  );
};

// ---------------------------------------------------------------------------
// Overwolf-Electron apps aren't tracked by the native store, so the shell
// detects them from the registry (+ a file-exists check). Resolves the set of
// installed Electron app UIDs; empty when the bridge is unavailable or errors.
export const getInstalledElectronAppIds = async (): Promise<Set<string>> => {
  try {
    const result = await sendRequest<string[]>(
      BridgeChannel.GetInstalledElectronApps,
    );
    return new Set(Array.isArray(result) ? result : []);
  } catch {
    return new Set();
  }
};

// ---------------------------------------------------------------------------
// Check whether a single Electron app (by UID) is installed. Used on app-page
// open so the page reflects current state. Resolves false on error/unavailable.
export const isElectronAppInstalled = async (uid: string): Promise<boolean> => {
  try {
    return (
      (await sendRequest<boolean>(BridgeChannel.IsElectronAppInstalled, {
        uid,
      })) === true
    );
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Launch an already-installed Electron app by UID (the shell runs its .exe).
// Fire-and-forget, mirroring installApp / launchApp.
export const launchElectronApp = (uid: string): void => {
  void sendRequest(BridgeChannel.LaunchElectron, { uid }).catch(
    () => undefined,
  );
};

// ---------------------------------------------------------------------------
// Sends an analytics event through the shell, which forwards it to the client's
// native RS tracking (the iframe has no `overwolfInternal` of its own). Only
// the event name and extra params are needed — the client attributes the rest
// itself. Resolves true when the shell confirmed the native API received it;
// false when it didn't (bridge unavailable, old client), so the caller can fall
// back to another channel.
export const trackOverwolfEvent = async (
  name: string,
  extra?: Record<string, unknown>,
): Promise<boolean> => {
  try {
    return (
      (await sendRequest<boolean>(BridgeChannel.Track, { name, extra })) ===
      true
    );
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// The name of the machine the shell is running on, read from the Windows
// registry by the shell. Used to label the Library by device when embedded.
// Resolves null when the bridge is unavailable or errors, so callers can fall
// back to a generic label.
export const getMachineName = async (): Promise<string | null> => {
  try {
    const result = await sendRequest<string | null>(
      BridgeChannel.GetMachineName,
    );
    return typeof result === 'string' && result.length > 0 ? result : null;
  } catch {
    return null;
  }
};

// -----------------------------------------------------------------------------
// --- Login -------------------------------------------------------------------
// Triggering login is a shell UI concern (open the native overlay) and uses a
// fire-and-forget `type` message. Logout is a request/response bridge call (the
// shell runs `OverwolfStore.login.logout()` and acks). Reading the signed-in
// user / reacting to login-state changes is done directly against the injected
// `overwolf.profile.*` API — see overwolf-api.service.

// ---------------------------------------------------------------------------
// Logs the user out via the shell's native login API. Resolves on the shell's
// ack and rejects on failure. The resulting login-state change should also be
// reflected by re-reading the profile.
export const requestOverwolfLogout = async (): Promise<void> => {
  await sendRequest(BridgeChannel.Logout);
};

// ---------------------------------------------------------------------------
// The signed-in user as reported by the native client: profile + auth token.
// Only the token is consumed today; the profile is typed loosely.
export interface OverwolfCurrentUser {
  status?: string;
  token?: string;
  profile?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Reads the currently signed-in user (profile + auth token) from the native
// client via the shell. Inside the Overwolf app the store runs in a cross-origin
// iframe with no `owUser` cookie, so this bridge call is the only way to obtain
// the auth token. Resolves null when the bridge is unavailable or errors, so
// callers can treat a missing token uniformly with the logged-out case.
export const getOverwolfCurrentUser =
  async (): Promise<OverwolfCurrentUser | null> => {
    try {
      const result = await sendRequest<OverwolfCurrentUser>(
        BridgeChannel.GetUserProfileDetails,
      );
      return result ?? null;
    } catch {
      return null;
    }
  };

// ---------------------------------------------------------------------------
// Theme for the shell's native modal overlays.
export type OverwolfOverlayTheme = 'Light' | 'Dark';

// ---------------------------------------------------------------------------
// Asks the shell to open its native in-app login overlay on top of the iframe.
// Fire-and-forget: the overlay closes itself, and the resulting login-state
// change is observed via `overwolf.profile.onLoginStateChanged`. No-op outside
// the shell (the shell ignores it unless our origin is allowlisted).
export const requestOverwolfLogin = (
  theme: OverwolfOverlayTheme = 'Dark',
): void => {
  if (typeof window === 'undefined' || !isEmbedded()) {
    return;
  }

  // targetOrigin '*' — we don't know the shell's overwolf-extension://… origin;
  // the shell validates the sender origin on receipt.
  window.parent.postMessage(
    { source: OW_STORE_MSG_SOURCE, type: SHOW_IN_APP_LOGIN_TYPE, theme },
    '*',
  );
};
